import { CommandType } from '../types';
import { AIConfig, AILevel, DEFAULT_AI_CONFIG } from './types';
import { CommandWeightMap } from './weightedSelection';

/**
 * スライダー値(0-100)をモディファイア倍率に変換する
 *
 * 線形補間:
 * - value=0   → minMultiplier
 * - value=50  → 1.0
 * - value=100 → maxMultiplier
 *
 * 範囲外の値は0-100にクランプされる。
 */
export function sliderToMultiplier(
  value: number,
  minMultiplier: number,
  maxMultiplier: number
): number {
  const clamped = Math.max(0, Math.min(100, value));

  if (clamped <= 50) {
    // 0→minMultiplier, 50→1.0
    return minMultiplier + (1.0 - minMultiplier) * (clamped / 50);
  } else {
    // 50→1.0, 100→maxMultiplier
    return 1.0 + (maxMultiplier - 1.0) * ((clamped - 50) / 50);
  }
}

/**
 * AIConfigからコマンド重みモディファイアマップを生成する
 *
 * 各スライダーが影響するコマンドの倍率を計算し、
 * 複数スライダーが同じコマンドに影響する場合は乗算する。
 * 全スライダー50のとき、全コマンド1.0（既存AIと同じ挙動）。
 *
 * 有効なパラメータ: aggression, specialPreference, distancePreference, stanceTendency, reflectorUsage
 * comboSynergy は現在未実装のため、この関数では使用されない。
 */
export function getAIConfigModifiers(config: AIConfig): CommandWeightMap {
  const mods: Record<string, number> = {};

  // 全コマンドを1.0で初期化
  for (const cmd of Object.values(CommandType)) {
    mods[cmd] = 1.0;
  }

  // aggression: 攻撃コマンドの重み
  const attackMul = sliderToMultiplier(config.aggression, 0.3, 2.5);
  mods[CommandType.WEAPON_ATTACK] *= attackMul;
  mods[CommandType.SPECIAL_ATTACK] *= attackMul;

  // specialPreference: 武器攻撃 vs 特殊攻撃のバランス
  const weaponSpecialMul = sliderToMultiplier(config.specialPreference, 0.4, 2.0);
  mods[CommandType.SPECIAL_ATTACK] *= weaponSpecialMul;
  const weaponMul = sliderToMultiplier(100 - config.specialPreference, 0.4, 2.0);
  mods[CommandType.WEAPON_ATTACK] *= weaponMul;
  // 攻撃性が高いとRETREATが下がる（逆相関、控えめな範囲でdistancePreferenceとの乗算時に突出しないよう抑制）
  const retreatAggrMul = sliderToMultiplier(100 - config.aggression, 0.7, 1.3);
  mods[CommandType.RETREAT] *= retreatAggrMul;

  // distancePreference: 距離志向
  // 0=近距離(ADVANCE高)、100=遠距離(RETREAT高)
  const advanceMul = sliderToMultiplier(100 - config.distancePreference, 0.3, 2.0);
  mods[CommandType.ADVANCE] *= advanceMul;
  const retreatDistMul = sliderToMultiplier(config.distancePreference, 0.3, 2.0);
  mods[CommandType.RETREAT] *= retreatDistMul;

  // stanceTendency: スタンス傾向
  // 0=守勢(STANCE_B高)、100=攻勢(STANCE_A高)
  const stanceAMul = sliderToMultiplier(config.stanceTendency, 0.4, 2.0);
  mods[CommandType.STANCE_A] *= stanceAMul;
  const stanceBMul = sliderToMultiplier(100 - config.stanceTendency, 0.4, 2.0);
  mods[CommandType.STANCE_B] *= stanceBMul;

  // reflectorUsage: リフレクター使用率
  const reflectorMul = sliderToMultiplier(config.reflectorUsage, 0.2, 2.5);
  mods[CommandType.REFLECTOR] *= reflectorMul;

  return mods as CommandWeightMap;
}

/**
 * モディファイアマップの効果をスライダー値でスケーリングする
 *
 * 各モディファイアの「1.0からの偏差」をscale倍する。
 * sliderValue=50→scale=1.0（元の効果そのまま）
 * sliderValue=0→scale=0.0（効果なし＝全て1.0）
 * sliderValue=100→scale=2.0（効果2倍）
 */
export function scaleModifiers(
  mods: CommandWeightMap,
  sliderValue: number
): CommandWeightMap {
  const scale = sliderValue / 50;
  const result: Record<string, number> = {};
  for (const [cmd, val] of Object.entries(mods)) {
    const deviation = (val ?? 1.0) - 1.0;
    result[cmd] = Math.max(0.1, 1.0 + deviation * scale);
  }
  return result as CommandWeightMap;
}

/**
 * フォールバック率をunpredictabilityで調整する
 *
 * フォールバック先はLv3（状況考慮あり）であり、完全ランダムではない。
 * unpredictability を高くするほどLv3相当の行動を取る頻度が増え、
 * 相手から見て「読みにくい」挙動になる。
 *
 * unpredictability=0  → 0%（フォールバックなし＝完全に最適行動）
 * unpredictability=50 → base*1.0（標準）
 * unpredictability=100→ 最大40%（上限クランプ）
 */
export function getAdjustedFallbackRate(
  baseFallbackRate: number,
  unpredictability: number
): number {
  const adjusted = baseFallbackRate * (unpredictability / 50);
  return Math.min(0.4, adjusted);
}

/**
 * AIConfig値のバリデーションとデフォルト適用
 *
 * - 未指定のプロパティにはデフォルト値を適用
 * - 範囲外の値を0-100にクランプ（thinkingLevelは有効なAILevelのみ）
 */
function isValidAILevel(level: unknown): level is AILevel {
  return Object.values(AILevel).includes(level as AILevel);
}

export function normalizeAIConfig(config: Partial<AIConfig>): AIConfig {
  const clamp = (val: number | undefined, defaultVal: number, min: number, max: number): number => {
    if (val === undefined) return defaultVal;
    return Math.max(min, Math.min(max, val));
  };

  return {
    aggression: clamp(config.aggression, DEFAULT_AI_CONFIG.aggression, 0, 100),
    distancePreference: clamp(config.distancePreference, DEFAULT_AI_CONFIG.distancePreference, 0, 100),
    stanceTendency: clamp(config.stanceTendency, DEFAULT_AI_CONFIG.stanceTendency, 0, 100),
    reflectorUsage: clamp(config.reflectorUsage, DEFAULT_AI_CONFIG.reflectorUsage, 0, 100),
    thinkingLevel: isValidAILevel(config.thinkingLevel) ? config.thinkingLevel! : DEFAULT_AI_CONFIG.thinkingLevel,
    specialPreference: clamp(config.specialPreference, DEFAULT_AI_CONFIG.specialPreference, 0, 100),
    riskTolerance: clamp(config.riskTolerance, DEFAULT_AI_CONFIG.riskTolerance, 0, 100),
    patternReading: clamp(config.patternReading, DEFAULT_AI_CONFIG.patternReading, 0, 100),
    counterTendency: clamp(config.counterTendency, DEFAULT_AI_CONFIG.counterTendency, 0, 100),
    adaptability: clamp(config.adaptability, DEFAULT_AI_CONFIG.adaptability, 0, 100),
    metaReading: clamp(config.metaReading, DEFAULT_AI_CONFIG.metaReading, 0, 100),
    unpredictability: clamp(config.unpredictability, DEFAULT_AI_CONFIG.unpredictability, 0, 100),
    comboSynergy: clamp(config.comboSynergy, DEFAULT_AI_CONFIG.comboSynergy, 0, 100),
    // Lv6パラメータ
    lookAheadDepth: clamp(config.lookAheadDepth, DEFAULT_AI_CONFIG.lookAheadDepth, 1, 5),
    opponentModeling: clamp(config.opponentModeling, DEFAULT_AI_CONFIG.opponentModeling, 0, 100),
    mixedStrategy: clamp(config.mixedStrategy, DEFAULT_AI_CONFIG.mixedStrategy, 0, 100),
    resourceOptimization: clamp(config.resourceOptimization, DEFAULT_AI_CONFIG.resourceOptimization, 0, 100),
  };
}
